import { Space, Table, TableColumnsType, Typography } from 'antd';
import { UserAddOutlined, ContainerOutlined, MinusCircleOutlined, EditOutlined } from '@ant-design/icons';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';
import UserAvatar from 'components/new-ui/base/avatar/UserAvatar';
import JobStatus from 'views/jobs/JobStatus';
import { GetJobsSortParams, JobResponse, JobStatuses } from 'store/jobs/job';
import { hasPermissions } from 'config/roles/hasPermissions';
import useSession from 'hooks/useSession';
import moment from 'moment';
import EmptyCell from 'components/new-ui/base/emtpy-cell/EmptyCell';
import TooltipWithCopy from 'components/ui/composed/tooltipWithCopy/TooltipWithCopy';
import { TablePaginationConfig, TableProps } from 'antd/es/table';
import { JobsTableKeys } from '../types';
import Button from 'components/new-ui/base/button/Button';
import { checkIsFreightForwarder } from 'config/roles/roles';
import { SortDirection, toAntOrder } from 'utils/tableHelpers';
import { TableRowSelection } from 'antd/lib/table/interface';
import EmailNotificationStatusIcon from 'components/ui/composed/email-notification/EmailNotificationStatusIcon';

type Props = {
    isUnderManifest?: boolean;
    jobs: JobResponse[];
    loading?: boolean;
    pagination?: false | TablePaginationConfig;
    onChange?: TableProps<JobResponse>['onChange'];
    onRowClick?: (record: JobResponse) => void;
    currentSortParam?: GetJobsSortParams;
    currentSortDirection?: SortDirection;
    optionalColumns?: {
        manifestId?: boolean;
        assignedTo?: boolean;
        emailNotification?: boolean;
    };
    rowSelection?: TableRowSelection<JobResponse> | undefined;
    actions?: {
        assign?: (job: JobResponse) => void;
        archive?: (job: JobResponse) => void;
        unarchive?: (job: JobResponse) => void;
        edit?: (job: JobResponse) => void;
        removeFromManifest?: (job: JobResponse, manifestId: string) => void;
    };
};

const JobsTable = ({
    isUnderManifest = false,
    jobs,
    loading,
    pagination,
    currentSortParam,
    currentSortDirection,
    onChange,
    onRowClick,
    optionalColumns,
    rowSelection,
    actions,
}: Props) => {
    const { userInfo } = useSession();
    const canUpdate = hasPermissions(userInfo, 'jobs', 'UPDATE');
    const isFreightForwarder = checkIsFreightForwarder(userInfo);

    const checkShowAssign = (record: JobResponse): boolean => {
        return !record.archived && isFreightForwarder && !!actions?.assign && canUpdate;
    };

    const columns: TableColumnsType<JobResponse> = [
        {
            title: 'Job ID',
            dataIndex: 'referenceId',
            key: GetJobsSortParams.ID,
            render: (jobReferenceId: string) => {
                const dataTestId = 'job-jobReferenceId-row';

                if (!jobReferenceId) {
                    return <EmptyCell dataTestId={dataTestId} />;
                }

                return (
                    <TooltipWithCopy dataTestId={dataTestId} copyText={jobReferenceId}>
                        {jobReferenceId}
                    </TooltipWithCopy>
                );
            },
            sorter: true,
            sortOrder: currentSortParam === GetJobsSortParams.ID ? toAntOrder(currentSortDirection) : null,
        },
        {
            title: 'Manifest ID',
            key: JobsTableKeys.MANIFEST_ID,
            render: (_, record: JobResponse) => {
                const dataTestId = 'job-manifestId-row';

                if (!record?.manifest?.referenceId) {
                    return <EmptyCell dataTestId={dataTestId} />;
                }

                return (
                    <TooltipWithCopy dataTestId={dataTestId} copyText={record?.manifest?.referenceId}>
                        {record?.manifest?.referenceId}
                    </TooltipWithCopy>
                );
            },
        },

        {
            title: 'PBN',
            key: 'PBN',
            render: (_, record: JobResponse) => {
                const dataTestId = 'job-pbn-row';

                if (!record?.externalReferences?.pbn) {
                    return <EmptyCell dataTestId={dataTestId} />;
                }

                return (
                    <TooltipWithCopy dataTestId={dataTestId} copyText={record?.externalReferences?.pbn}>
                        {record?.externalReferences?.pbn}
                    </TooltipWithCopy>
                );
            },
        },
        {
            title: 'GMR',
            key: 'GMR',
            render: (_, record: JobResponse) => {
                const dataTestId = 'job-gmr-row';

                if (!record?.externalReferences?.gmr) {
                    return <EmptyCell dataTestId={dataTestId} />;
                }

                return (
                    <TooltipWithCopy dataTestId={dataTestId} copyText={record?.externalReferences?.gmr}>
                        {record?.externalReferences?.gmr}
                    </TooltipWithCopy>
                );
            },
        },

        {
            title: 'Customer',
            dataIndex: ['customer', 'name'],
            key: JobsTableKeys.CUSTOMER_NAME,
            render: (customerName: string) => {
                const dataTestId = 'job-customerName-row';

                if (!customerName) {
                    return <EmptyCell dataTestId={dataTestId} />;
                }

                return <Typography.Text data-testid={dataTestId}>{customerName}</Typography.Text>;
            },
        },
        {
            title: 'INCOTERM',
            dataIndex: 'incotermCode',
            key: JobsTableKeys.INCOTERM,
            align: 'center',
            render: (incotermCode: string) => {
                const dataTestId = 'job-incoterm-row';

                if (!incotermCode) {
                    return <EmptyCell dataTestId={dataTestId} />;
                }

                return <Typography.Text data-testid={dataTestId}>{incotermCode}</Typography.Text>;
            },
        },
        {
            title: 'Declarations',
            dataIndex: 'declarationCount',
            key: JobsTableKeys.DECLARATIONS,
            align: 'right',
            render: (declarationsCount: number) => {
                const dataTestId = 'job-declaratounsCount-row';

                if (typeof declarationsCount !== 'number') {
                    return <EmptyCell dataTestId={dataTestId} />;
                }

                return <Typography.Text data-testid={dataTestId}>{declarationsCount}</Typography.Text>;
            },
        },
        {
            title: 'Created By',
            key: GetJobsSortParams.CREATED_AT,
            render: (_, record: JobResponse) => {
                const dataTestId = 'job-createdBy-row';
                const formattedCreatedAt = moment(record.createdAt).format('DD/MM/YY hh:mm A');

                return (
                    <Space direction="vertical">
                        <Typography.Text data-testid={dataTestId}>{record.creator.fullName ?? '-'}</Typography.Text>
                        <Typography.Text data-testid={`${dataTestId}-createdAt`}>{formattedCreatedAt}</Typography.Text>
                    </Space>
                );
            },
            sorter: true,
            sortOrder: currentSortParam === GetJobsSortParams.CREATED_AT ? toAntOrder(currentSortDirection) : null,
        },
        {
            title: 'Assigned To',
            dataIndex: 'assignedTo',
            key: JobsTableKeys.ASSIGNED_TO,
            render: (_, record: JobResponse) => {
                const dataTestId = 'job-assigned-to-row';

                if (!record?.owner?.fullName) {
                    if (checkShowAssign(record)) {
                        return (
                            <Button
                                title="Assign Job"
                                type="assign"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    actions?.assign?.(record);
                                }}
                            />
                        );
                    } else {
                        return <Typography.Text data-testid={dataTestId}>-</Typography.Text>;
                    }
                }

                return (
                    <Space size={8}>
                        <UserAvatar userName={record?.owner.fullName} src={record?.owner.profilePictureLink} />
                        <span data-testid={dataTestId}>{record?.owner.fullName}</span>
                    </Space>
                );
            },
        },
        {
            width: 1,
            key: JobsTableKeys.EMAIL_NOTIFICATION,
            render: (_, record: JobResponse) => {
                const dataTestId = 'job-emailNotification-row';
                const overallStatus = record?.emailStatus?.overallStatus;
                const providerStatus = record?.emailStatus?.overallProviderEventType;

                if (!overallStatus) return null;

                return (
                    <EmailNotificationStatusIcon
                        overallStatus={overallStatus}
                        providerStatus={providerStatus}
                        dataTestId={dataTestId}
                    />
                );
            },
        },
        {
            title: 'Job Status',
            dataIndex: 'status',
            key: GetJobsSortParams.STATUS,
            render: (status: JobStatuses) => {
                const dataTestId = 'job-status-row';
                if (!status) {
                    return <EmptyCell dataTestId={dataTestId} />;
                }
                return <JobStatus status={status} dataTestId={dataTestId} />;
            },
            sorter: true,
            sortOrder: currentSortParam === GetJobsSortParams.STATUS ? toAntOrder(currentSortDirection) : null,
        },

        {
            title: 'Actions',
            key: JobsTableKeys.ACTIONS,
            render: (_, record) => {
                const hasArchiveAccess = hasPermissions(userInfo, 'jobs', 'ARCHIVE');
                const canBeArchived = [
                    JobStatuses.NEW,
                    JobStatuses.REJECTED,
                    JobStatuses.CLOSED,
                    JobStatuses.COMPLETED,
                ].includes(record.status);

                const isArchived = record.archived;
                const disabledArchive = !hasArchiveAccess || !canBeArchived;
                const isDraftJob = record.status === JobStatuses.NEW;
                const manifestId = record?.manifest?.id;

                const stopRowClick = (fn: () => void) => (e: React.MouseEvent) => {
                    e.stopPropagation();
                    fn();
                };

                const showAssign = checkShowAssign(record);
                const showEdit = !isArchived && isDraftJob && !!actions?.edit;
                const showUnarchive = isArchived && !!actions?.unarchive;
                const showArchive = !isArchived && !!actions?.archive;
                const showRemoveFromManifest =
                    !isArchived && isUnderManifest && canBeArchived && !!manifestId && !!actions?.removeFromManifest;

                return (
                    <Space>
                        {showAssign && (
                            <IconButton
                                icon={<UserAddOutlined />}
                                tooltipText="Assign Job"
                                onClick={stopRowClick(() => actions?.assign?.(record))}
                            />
                        )}
                        {showEdit && (
                            <IconButton
                                icon={<EditOutlined />}
                                tooltipText="Edit job"
                                onClick={stopRowClick(() => actions.edit?.(record))}
                            />
                        )}
                        {showUnarchive && (
                            <IconButton
                                icon={<ContainerOutlined />}
                                tooltipText="Unarchive Job"
                                onClick={stopRowClick(() => actions?.unarchive?.(record))}
                                disabled={disabledArchive}
                            />
                        )}
                        {showArchive && (
                            <IconButton
                                icon={<ContainerOutlined />}
                                tooltipText="Archive Job"
                                disabledText="Can not archive job with this status"
                                onClick={stopRowClick(() => actions?.archive?.(record))}
                                disabled={disabledArchive}
                            />
                        )}
                        {showRemoveFromManifest && (
                            <IconButton
                                icon={<MinusCircleOutlined />}
                                tooltipText="Remove job from manifest"
                                onClick={stopRowClick(
                                    () =>
                                        record?.manifest?.id &&
                                        actions?.removeFromManifest?.(record, record.manifest.id)
                                )}
                                disabled={!hasArchiveAccess}
                            />
                        )}
                    </Space>
                );
            },
        },
    ];

    const hasEmailNotifications = jobs.some((job) => !!job?.emailStatus?.overallStatus);

    const filteredColumns = columns.filter((col) => {
        if (!canUpdate && col.key === JobsTableKeys.ACTIONS) {
            return false;
        }

        if (
            (!hasEmailNotifications || !optionalColumns?.emailNotification) &&
            col.key === JobsTableKeys.EMAIL_NOTIFICATION
        ) {
            return false;
        }

        if (!optionalColumns?.manifestId && col.key === JobsTableKeys.MANIFEST_ID) {
            return false;
        }

        // Display these columns only for standalone job
        if (isUnderManifest && (col.key === 'PBN' || col.key === 'GMR')) {
            return false;
        }

        if (!optionalColumns?.assignedTo && col.key === JobsTableKeys.ASSIGNED_TO) {
            return false;
        }

        return true;
    });

    return (
        <StyledTableWrapper>
            <Table
                loading={loading}
                rowKey="id"
                onRow={(record) => ({
                    onClick: () => onRowClick?.(record),
                    style: { cursor: 'pointer' },
                })}
                rowClassName="row-align-top"
                dataSource={jobs}
                columns={filteredColumns}
                components={{
                    header: {
                        cell: (props: any) => <StyledTableHeaderCell {...props} />,
                    },
                }}
                rowSelection={rowSelection}
                onChange={onChange}
                pagination={pagination}
                scroll={{ x: 'max-content' }}
            />
        </StyledTableWrapper>
    );
};

export default JobsTable;
